import type { AppType } from '../types';

// All valid app types for URL routing
const VALID_APP_TYPES: AppType[] = [
  'my-computer', 'recycle-bin', 'projects', 'about-me', 'contact',
  'resume', 'blog', 'settings', 'notepad', 'minesweeper',
  'solitaire', 'internet-explorer', 'msn-messenger',
  'command-prompt', 'minecraft', 'research',
];

export interface ParsedRoute {
  appType: AppType | null;
  subPath: string | null;
}

/**
 * Parse a URL pathname into an app type and optional sub-path.
 *
 * Examples:
 *   '/' → { appType: null, subPath: null }
 *   '/research' → { appType: 'research', subPath: null }
 *   '/research/ieee-paper' → { appType: 'research', subPath: 'ieee-paper' }
 *   '/my-computer/Local%20Disk%20(C:)' → { appType: 'my-computer', subPath: 'Local Disk (C:)' }
 *   '/my-computer/Local%20Disk%20(C:)/Program%20Files' → { appType: 'my-computer', subPath: 'Local Disk (C:)/Program Files' }
 */
export function parseRoute(pathname: string): ParsedRoute {
  // Remove leading/trailing slashes and decode
  const cleaned = pathname.replace(/^\/+|\/+$/g, '');

  if (!cleaned) {
    return { appType: null, subPath: null };
  }

  // Split on first slash to get app type, rest is sub-path
  const firstSlash = cleaned.indexOf('/');
  const appSlug = firstSlash === -1 ? cleaned : cleaned.slice(0, firstSlash);
  const rest = firstSlash === -1 ? null : cleaned.slice(firstSlash + 1);

  // Validate app type
  const appType = VALID_APP_TYPES.find(t => t === appSlug) || null;

  if (!appType) {
    return { appType: null, subPath: null };
  }

  // Decode the sub-path segments
  const subPath = rest ? decodeURIComponent(rest) : null;

  return { appType, subPath };
}

/**
 * Build a URL pathname from an app type and optional sub-path.
 *
 * Examples:
 *   (null) → '/'
 *   ('research', null) → '/research'
 *   ('research', 'ieee-paper') → '/research/ieee-paper'
 *   ('my-computer', 'Local Disk (C:)/Program Files') → '/my-computer/Local%20Disk%20(C%3A)/Program%20Files'
 */
export function buildRoute(appType: AppType | null, subPath?: string | null): string {
  if (!appType) {
    return '/';
  }

  if (!subPath) {
    return `/${appType}`;
  }

  // Encode each segment of the sub-path individually
  const encodedSubPath = subPath
    .split('/')
    .map(segment => encodeURIComponent(segment))
    .join('/');

  return `/${appType}/${encodedSubPath}`;
}

/**
 * Check if a URL pathname has a route (i.e., is not just '/')
 * Used to determine whether to skip the boot sequence.
 */
export function hasRoute(pathname: string): boolean {
  const { appType } = parseRoute(pathname);
  return appType !== null;
}

/**
 * Convert a MyComputer internal path array to a sub-path string.
 * e.g., ['Local Disk (C:)', 'Program Files'] → 'Local Disk (C:)/Program Files'
 */
export function myComputerPathToSubPath(path: string[]): string | null {
  if (path.length === 0) return null;
  return path.join('/');
}

/**
 * Convert a sub-path string to a MyComputer internal path array.
 * e.g., 'Local Disk (C:)/Program Files' → ['Local Disk (C:)', 'Program Files']
 */
export function subPathToMyComputerPath(subPath: string | null): string[] {
  if (!subPath) return [];
  return subPath.split('/');
}
